import type { DeficiencySubject } from "../../shared/services/deficiencies";

// Which deficiency subject a feature's revisions cure; the new-draft dialog lists that subject.
export const deficiencySubjectForFeature = (featureKey: string): DeficiencySubject => {
  if (
    featureKey === "project-generation" ||
    featureKey === "image-generation" ||
    featureKey.startsWith("presentation-")
  ) {
    return "memo_chapter";
  }
  if (featureKey === "projects-list-chat") return "projects_list_chat";
  return "case_brief";
};

export type TActiveRevision = {
  id: number;
  revisionNumber: number | null;
  activatedAt: string | null;
  note: string | null;
} | null;

export type TRevisionStatus = "draft" | "active" | "inactive" | "discarded";

export const GENERATION_MODELS = ["haiku", "sonnet", "opus"] as const;

export type TGenerationModel = (typeof GENERATION_MODELS)[number];

export const DEFAULT_GENERATION_MODEL: TGenerationModel = "sonnet";

export const GENERATION_MODEL_HINTS: Record<TGenerationModel, string> = {
  haiku: "Cheapest and fastest; for cheap structural checks, not a real business case",
  sonnet: "The default: what every generation ran on before this setting existed",
  opus: "Most capable and by far the most expensive — long business-case runs"
};

export type TRevisionSummary = {
  id: number;
  revisionNumber: number | null;
  featureKey: string;
  status: TRevisionStatus;
  note: string | null;
  parentRevisionId: number | null;
  parentRevisionNumber: number | null;
  deficiencyId: number | null;
  // Project-generation only; null for every other feature and for revisions predating the column.
  model: string | null;
  entryCount: number;
  createdById: number | null;
  createdByName: string | null;
  createdAt: string;
  activatedById: number | null;
  activatedByName: string | null;
  activatedAt: string | null;
};

export type TPromptLint = {
  missingRequired: string[];
  unknown: string[];
};

export type TRebasePlan = {
  needed: boolean;
  activeRevisionId: number | null;
  activeRevisionNumber: number | null;
  conflicting: string[];
  incoming: string[];
  keeping: string[];
};

export type TSkillFileDetail = {
  path: string;
  content: string;
  activeContent: string;
  differsFromActive: boolean;
};

export type TRevisionEntry = {
  promptKey: string;
  label: string;
  description: string;
  content: string;
  activeContent: string;
  differsFromActive: boolean;
  editedSinceBase: boolean;
  requiredPlaceholders: string[];
  optionalPlaceholders: string[];
  featureFlag: string | null;
  lint: TPromptLint;
  files: TSkillFileDetail[];
};

export const PROJECT_GENERATION_FEATURE_KEY = "project-generation";
export const LEO_CHAT_FEATURE_KEY = "leo-chat";

// The prompt of each feature that is a skill folder (SKILL.md plus files), edited as an archive.
export const SKILL_FOLDER_PROMPT_KEYS: Record<string, string> = {
  [PROJECT_GENERATION_FEATURE_KEY]: "skill",
  [LEO_CHAT_FEATURE_KEY]: "skill-instructions"
};

// A registered prompt the revision doesn't store, so it resolves to the code's text.
export type TCodeDefaultPrompt = {
  promptKey: string;
  label: string;
  description: string;
  content: string;
  featureFlag: string | null;
};

export type TRevisionDetail = TRevisionSummary & {
  entries: TRevisionEntry[];
  keysResolvedFromDefault: string[];
  codeDefaultPrompts: TCodeDefaultPrompt[];
  unregisteredKeys: string[];
  rebase: TRebasePlan | null;
  pinnedRevisionId: number | null;
  pinnedRevisionNumber: number | null;
  passedTestCaseCount: number;
  requiredPassedTestCaseCount: number;
  supportsTests: boolean;
  supportsModel: boolean;
};

export const STATUS_STYLES: Record<TRevisionStatus, string> = {
  draft: "bg-blue-100 text-blue-800",
  active: "bg-green-100 text-green-800",
  inactive: "bg-gray-100 text-gray-600",
  discarded: "bg-gray-100 text-gray-400"
};

export function formatRevisionLabel(revision: TActiveRevision): string {
  if (!revision) return "No revision — code defaults";

  const number = revision.revisionNumber ?? revision.id;
  const activatedAt = revision.activatedAt ? new Date(revision.activatedAt).toLocaleDateString() : null;
  return activatedAt ? `Revision ${number} · active since ${activatedAt}` : `Revision ${number} · active`;
}

export function formatTimestamp(value: string | null): string {
  return value ? new Date(value).toLocaleString() : "—";
}

export function hasLintErrors(lint: TPromptLint): boolean {
  return lint.missingRequired.length > 0 || lint.unknown.length > 0;
}
